import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { LogService } from './log.service';

/**
 * Fallback tax rates by US state (as decimal, e.g., 0.08 = 8%)
 * Used only when server is unavailable
 */
const US_STATE_TAX_RATES: Record<string, number> = {
	AZ: 0.056, // Arizona base rate (5.6%)
	CA: 0.0725,
	NY: 0.04,
	TX: 0.0625,
	FL: 0.06
};

/**
 * ZIP-based overrides for US localities (fallback only)
 */
const US_ZIP_TOTAL_TAX_RATES: Array<{
	state: string;
	zip3: string;
	rate: number;
}> = [
	// Phoenix, AZ (85006 area) - 9.1% combined rate
	{ state: 'AZ', zip3: '850', rate: 0.091 },
	// NYC
	{ state: 'NY', zip3: '100', rate: 0.08875 },
	{ state: 'NY', zip3: '101', rate: 0.08875 },
	// Chicago
	{ state: 'IL', zip3: '606', rate: 0.1025 }
];

const COUNTRY_TAX_RATES: Record<string, number> = {
	US: 0.08,
	CA: 0.13,
	GB: 0.2,
	AU: 0.1
};

@Injectable({
	providedIn: 'root'
})
export class TaxService {
	constructor(
		private apiService: ApiService,
		private log: LogService
	) {}

	/**
	 * Calculates tax rate based on billing address using server API
	 * Falls back to local calculation if server is unavailable
	 * @param billingAddress The billing address object
	 * @returns Tax rate as a decimal (e.g., 0.08 for 8%)
	 */
	async calculateTaxRate(billingAddress: {
		state?: string;
		postal_code?: string;
		country?: string;
		city?: string;
	}): Promise<number> {
		if (!billingAddress) {
			return 0.08; // Default 8% if no address
		}

		try {
			// Try to get tax rate from server
			const response = await this.apiService.getTaxRate(billingAddress);
			this.log.debug('Tax rate from server:', response);
			return response.rate;
		} catch (error) {
			// Fallback to local calculation if server fails
			this.log.warn('Server tax calculation failed, using fallback:', error);
			return this.calculateTaxRateFallback(billingAddress);
		}
	}

	/**
	 * Fallback tax rate calculation (used when server is unavailable)
	 */
	private calculateTaxRateFallback(billingAddress: { state?: string; postal_code?: string; country?: string }): number {
		const country = billingAddress.country?.toUpperCase();
		const state = billingAddress.state?.toUpperCase();
		const postalCode = billingAddress.postal_code;

		// For US addresses, use state-specific rates
		if (country === 'US' && state) {
			// Check for ZIP-specific overrides
			if (postalCode && postalCode.length >= 3) {
				const zip3 = postalCode.slice(0, 3);
				const match = US_ZIP_TOTAL_TAX_RATES.find(x => x.state === state && x.zip3 === zip3);
				if (match) return match.rate;
			}

			const stateRate = US_STATE_TAX_RATES[state];
			if (stateRate !== undefined) {
				return stateRate;
			}
			return COUNTRY_TAX_RATES['US'] || 0.08;
		}

		// For other countries
		if (country && COUNTRY_TAX_RATES[country]) {
			return COUNTRY_TAX_RATES[country];
		}

		return 0;
	}

	/**
	 * Calculates tax amount based on subtotal and billing address
	 * @param subtotal The subtotal amount
	 * @param billingAddress The billing address object
	 * @returns Tax amount rounded to 2 decimal places
	 */
	async calculateTax(
		subtotal: number,
		billingAddress: {
			state?: string;
			postal_code?: string;
			country?: string;
			city?: string;
			line1?: string;
		}
	): Promise<number> {
		try {
			// Try to get tax calculation from server
			const response = await this.apiService.calculateTax(billingAddress, subtotal);
			this.log.debug('Tax calculation from server:', response);
			return response.taxAmount;
		} catch (error) {
			// Fallback to local calculation if server fails
			this.log.warn('Server tax calculation failed, using fallback:', error);
			const taxRate = await this.calculateTaxRate(billingAddress);
			return Math.round(subtotal * taxRate * 100) / 100;
		}
	}
}
